import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';

interface ProgressBarProps {
  current: number;
  total: number;
  className?: string;
  showPercentage?: boolean;
  showCount?: boolean;
  size?: 'small' | 'medium' | 'large';
  animationDuration?: number;
  // Add a unique key to help identify new validation sessions
  validationKey?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  current,
  total,
  className = '',
  showPercentage = true,
  showCount = false,
  size = 'medium',
  animationDuration = 300,
  validationKey = 'default'
}) => {
  const [displayedProgress, setDisplayedProgress] = useState(0);
  const [isResetting, setIsResetting] = useState(false);
  
  // Use refs to track previous values and validation session
  const prevProgressRef = useRef(0);
  const prevValidationKeyRef = useRef(validationKey);
  const resetTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Calculate progress percentage with safety checks
  const progress = useMemo(() => {
    // Handle edge cases where total might be 0 or negative
    if (total <= 0) return 0;
    if (current < 0) return 0;
    
    // Calculate percentage and cap at 100%
    return Math.min((current / total) * 100, 100);
  }, [current, total]);

  // Debounced progress update to prevent excessive re-renders with adaptive throttling
  const debouncedProgressUpdate = useCallback((newProgress: number) => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    // Adaptive update rate based on progress change magnitude
    const progressDelta = Math.abs(newProgress - prevProgressRef.current);
    let updateDelay = 16; // Default 60fps
    
    // For rapid changes (like non-API validation), use longer delays to ensure visibility
    if (progressDelta > 10) {
      updateDelay = 50; // Slower updates for big jumps
    } else if (progressDelta > 5) {
      updateDelay = 30; // Medium delay for medium jumps
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      setDisplayedProgress(newProgress);
    }, updateDelay);
  }, []);

  useEffect(() => {
    const prevProgress = prevProgressRef.current;
    const prevValidationKey = prevValidationKeyRef.current;
    
    // Detect new validation session - this is the most reliable way to reset
    const isNewValidationSession = validationKey !== prevValidationKey;
    
    // Detect significant backward progress (but allow small fluctuations)
    const hasSignificantBackwardProgress = progress < (prevProgress - 5); // 5% tolerance
    
    // Detect reset conditions
    const shouldReset = isNewValidationSession || 
                       (current === 0 && prevProgress > 10) || // Reset only if we had significant progress
                       hasSignificantBackwardProgress;

    if (shouldReset && !isResetting) {
      setIsResetting(true);
      setDisplayedProgress(0);
      
      // Clear any pending updates
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      
      // Start new progress after a brief reset animation
      resetTimeoutRef.current = setTimeout(() => {
        setIsResetting(false);
        debouncedProgressUpdate(progress);
      }, 150); // Slightly longer reset delay for visual clarity
      
    } else if (!isResetting) {
      // Normal progress update - only update if progress actually changed
      if (Math.abs(progress - prevProgress) > 0.1) { // Minimum threshold to prevent micro-updates
        debouncedProgressUpdate(progress);
      }
    }
    
    // Update refs for next comparison
    prevProgressRef.current = progress;
    prevValidationKeyRef.current = validationKey;
    
    // Cleanup function
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [current, total, progress, validationKey, isResetting, debouncedProgressUpdate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={`progress-bar-wrapper ${className}`}>
      {/* Progress bar container */}
      <div className={`progress-bar-container progress-bar--${size}`}>
        {/* Background shimmer effect */}
        <div className="progress-bar__background-shimmer" />
        
        {/* Progress fill with smoother transitions */}
        <div 
          className="progress-bar__fill"
          style={{
            width: `${displayedProgress}%`,
            transitionDuration: isResetting ? '150ms' : `${animationDuration}ms`,
            transitionTimingFunction: isResetting ? 'ease-out' : 'ease-in-out'
          }}
        >
          {/* Animated shimmer effect on progress bar */}
          <div className="progress-bar__shimmer" />
          
          {/* Glowing effect */}
          <div className="progress-bar__glow" />
        </div>

        {/* Leading edge glow - only show when actively progressing */}
        {displayedProgress > 0 && displayedProgress < 100 && !isResetting && (
          <div 
            className="progress-bar__leading-edge"
            style={{
              left: `${displayedProgress}%`,
              transitionDuration: `${animationDuration}ms`
            }}
          />
        )}
      </div>

      {/* Progress text with better formatting */}
      {(showPercentage || showCount) && (
        <div className="progress-bar__text">
          <div className="progress-bar__count">
            {showCount && (
              <span>
                {current.toLocaleString()} of {total.toLocaleString()} items
              </span>
            )}
          </div>
          <div className="progress-bar__percentage">
            {showPercentage && (
              <span>
                {Math.round(progress)}%
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressBar;